import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import CloudflareImage from '../components/ui/cloudflare-image';
import { getCoinActivity } from '../services/api'; // Assuming an API service exists
import { Button } from '../components/ui/button';

const NavbarSpacer = styled.div`
  height: 120px;
  @media (min-width: 768px) {
    height: 140px;
  }
  @media (min-width: 1024px) {
    height: 160px;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #fff;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 40px 16px;
  
  @media (min-width: 768px) {
    padding: 40px 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 40px 24px;
  }
  
  @media (min-width: 1536px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 1px;
  margin: 0;
  text-transform: uppercase;
`;

const Balance = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 500;
`;

const CoinIcon = styled(CloudflareImage)`
  width: 24px;
  height: 24px;
`;

const TransactionsContainer = styled.div`
  min-height: 300px;
`;

const NoTransactions = styled.div`
  text-align: center;
  padding: 60px 0;
  font-size: 18px;
  color: #666;
  font-style: italic;
`;

const TransactionsList = styled.div`
  width: 100%;
`;

const TransactionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

const TransactionInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const TransactionTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 4px;
`;

const TransactionDate = styled.div`
  font-size: 12px;
  color: #666;
`;

const TransactionAmount = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.type === 'credit' ? '#28a745' : '#dc3545'};
`;

const CoinActivityPage = () => {
  const [activity, setActivity] = useState({ transactions: [], balance: 0 });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [transactionsPerPage] = useState(10);

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        setLoading(true);
        const response = await getCoinActivity();
        if (response.success) {
          setActivity(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch coin activity", error);
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, []);

  return (
    <PageContainer>
      <NavbarSpacer />
      <ContentWrapper>
        <PageHeader>
          <Title>MAD Coin Activity</Title>
          <Balance>
            Current Balance: {activity.balance.toFixed(2)}
            <CoinIcon src="/images/madicon.png" alt="MAD Coin" />
          </Balance>
        </PageHeader>
        
        <TransactionsContainer>
          {loading ? (
            <p>Loading activity...</p>
          ) : activity.transactions.length > 0 ? (
            <>
              <TransactionsList>
                {[...activity.transactions]
                  .reverse()
                  .slice((currentPage - 1) * transactionsPerPage, currentPage * transactionsPerPage)
                  .map((transaction) => (
                    <TransactionItem key={transaction._id}>
                      <TransactionInfo>
                        <TransactionTitle>{transaction.description}</TransactionTitle>
                        <TransactionDate>{new Date(transaction.createdAt).toLocaleDateString()}</TransactionDate>
                      </TransactionInfo>
                      <TransactionAmount type={transaction.type}>
                        {transaction.type === 'credit' ? '+' : '-'}{transaction.amount.toFixed(2)}
                        <CoinIcon src="/images/madicon.png" alt="MAD Coin" />
                      </TransactionAmount>
                    </TransactionItem>
                  ))}
              </TransactionsList>
              <div className="flex justify-center items-center space-x-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span>
                  Page {currentPage} of {Math.ceil(activity.transactions.length / transactionsPerPage)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(activity.transactions.length / transactionsPerPage)))}
                  disabled={currentPage === Math.ceil(activity.transactions.length / transactionsPerPage)}
                >
                  Next
                </Button>
              </div>
            </>
          ) : (
            <NoTransactions>No Transactions</NoTransactions>
          )}
        </TransactionsContainer>
      </ContentWrapper>
    </PageContainer>
  );
};

export default CoinActivityPage;
